import React from 'react';
import { NavLink } from 'react-router-dom';
import pageStyles from '../../styles/pages.module.css';

/**
 * PageToolbar - Unified toolbar for page navigation (tabs) and page-level actions
 *
 * Use cases:
 * 1. Tabs only - navigation between page sections
 * 2. Tabs + page actions - navigation + buttons like "Edit in Wizard"
 * 3. 2-level - primary selectors (dropdowns) above, secondary tabs below
 *
 * NOTE: For table-specific actions (add, delete, import), use pageStyles.tableActions
 * directly above the table instead.
 *
 * RTL layout: Tabs on right, actions on left
 *
 * @param {Array} tabs - Array of tab objects: { id, label, onClick?, to? (for NavLink) }
 * @param {string} activeTab - ID of currently active tab (for button tabs)
 * @param {function} onTabChange - Callback when tab clicked: (tabId) => void
 * @param {React.ReactNode} actions - Page-level action buttons (not table actions)
 * @param {React.ReactNode} primarySelectors - Primary selectors (dropdowns) for 2-level navigation
 * @param {string} className - Optional additional class
 */
const PageToolbar = ({
  tabs,
  activeTab,
  onTabChange,
  actions,
  primarySelectors,
  className = '',
}) => {
  // If we have primary selectors, render 2-level toolbar
  if (primarySelectors) {
    return (
      <div className={`${pageStyles.toolbarStack} ${className}`}>
        {/* Primary level: selectors */}
        <div className={pageStyles.toolbarPrimary}>{primarySelectors}</div>

        {/* Secondary level: tabs + actions */}
        {(tabs || actions) && (
          <div className={pageStyles.toolbar}>
            {tabs && (
              <div className={pageStyles.tabBar}>
                {tabs.map(tab =>
                  tab.to ? (
                    <NavLink
                      key={tab.id}
                      to={tab.to}
                      end
                      className={({ isActive }) =>
                        isActive ? pageStyles.tabActive : pageStyles.tab
                      }
                    >
                      {tab.label}
                    </NavLink>
                  ) : (
                    <button
                      key={tab.id}
                      className={activeTab === tab.id ? pageStyles.tabActive : pageStyles.tab}
                      onClick={() => {
                        if (tab.onClick) tab.onClick();
                        if (onTabChange) onTabChange(tab.id);
                      }}
                    >
                      {tab.label}
                    </button>
                  ),
                )}
              </div>
            )}
            {actions && <div className={pageStyles.toolbarActions}>{actions}</div>}
          </div>
        )}
      </div>
    );
  }

  // Single-level toolbar: tabs + actions in same row
  // In RTL: tabs render first (right side), actions render second (left side)
  return (
    <div className={`${pageStyles.toolbar} ${className}`}>
      {tabs && (
        <div className={pageStyles.tabBar}>
          {tabs.map(tab =>
            tab.to ? (
              <NavLink
                key={tab.id}
                to={tab.to}
                end
                className={({ isActive }) => (isActive ? pageStyles.tabActive : pageStyles.tab)}
              >
                {tab.label}
              </NavLink>
            ) : (
              <button
                key={tab.id}
                className={activeTab === tab.id ? pageStyles.tabActive : pageStyles.tab}
                onClick={() => {
                  if (tab.onClick) tab.onClick();
                  if (onTabChange) onTabChange(tab.id);
                }}
              >
                {tab.label}
              </button>
            ),
          )}
        </div>
      )}
      {actions && <div className={pageStyles.toolbarActions}>{actions}</div>}
    </div>
  );
};

export default PageToolbar;
